import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './AdminDashboardPage.css';

const BASE = (process.env.REACT_APP_API_URL || 'https://halfprice-ca-v2.onrender.com/api').replace(/\/+$/, '');

const adminReq = async (path, options = {}) => {
  const token = localStorage.getItem('hp_token');
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers },
    ...options,
  });
  if (options.raw) return res;
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
};

const SOURCE_OPTIONS = [
  { value: 'website',       label: 'Through Website' },
  { value: 'google',        label: 'Through Google' },
  { value: 'referral',      label: 'Through Referral' },
  { value: 'social-media',  label: 'Through Social Media' },
  { value: 'word-of-mouth', label: 'Word of Mouth' },
  { value: 'cold-outreach', label: 'Cold Outreach by Us' },
  { value: 'coming-soon',   label: 'Coming Soon Form' },
  { value: 'other',         label: 'Other' },
];

const STATUS_BG  = { new:'#FAECE7', contacted:'#FEF3C7', converted:'#E3F4EB', lost:'#FFF0EE' };
const PLAN_BG    = { free:'var(--bg-subtle)', starter:'#FAECE7', pro:'#E3F4EB' };
const LIMIT_OPTS = [10, 25, 50, 100];

const CUISINES   = ['canadian','indian','chinese','italian','mexican','japanese','thai','vietnamese','middle-eastern','greek','korean','american','seafood','pizza','sushi','vegetarian','vegan','breakfast','bakery','other'];
const HOODS      = ['Downtown','St. Vital','River Heights','Transcona','North End','Osborne Village','Wolseley','West End','St. James','Elmwood','Fort Garry','Tuxedo'];
const MEAL_TYPES = ['Dine-in','Takeout','Delivery'];

// ── Helpers ───────────────────────────────────────────────────────────
function SortTh({ col, label, sort, order, onSort }) {
  const active = sort === col;
  return (
    <th className={`sortable-th${active ? ' sort-active' : ''}`} onClick={() => onSort(col)}>
      {label} <span className="sort-arrow">{active ? (order==='asc'?'↑':'↓') : '↕'}</span>
    </th>
  );
}

function Pagination({ page, pages, total, limit, onPage, onLimit }) {
  const start = total === 0 ? 0 : (page-1)*limit+1;
  const end   = Math.min(page*limit, total);
  const nums  = [];
  if (pages <= 7) { for (let i=1;i<=pages;i++) nums.push(i); }
  else if (page <= 4) { for (let i=1;i<=5;i++) nums.push(i); nums.push('…'); nums.push(pages); }
  else if (page >= pages-3) { nums.push(1); nums.push('…'); for (let i=pages-4;i<=pages;i++) nums.push(i); }
  else { nums.push(1); nums.push('…'); for (let i=page-1;i<=page+1;i++) nums.push(i); nums.push('…'); nums.push(pages); }

  return (
    <div className="admin-pg">
      <span className="pg-info">Showing <b>{start}–{end}</b> of <b>{total}</b></span>
      <div className="pg-controls">
        <select className="pg-limit" value={limit} onChange={e=>{onLimit(parseInt(e.target.value));}}>
          {LIMIT_OPTS.map(l=><option key={l} value={l}>Show {l}</option>)}
        </select>
        <button className="pg-btn" disabled={page<=1} onClick={()=>onPage(1)}>«</button>
        <button className="pg-btn" disabled={page<=1} onClick={()=>onPage(page-1)}>‹</button>
        {nums.map((n,i)=> n==='…'
          ? <span key={`e${i}`} className="pg-ellipsis">…</span>
          : <button key={n} className={`pg-btn${page===n?' active':''}`} onClick={()=>onPage(n)}>{n}</button>
        )}
        <button className="pg-btn" disabled={page>=pages} onClick={()=>onPage(page+1)}>›</button>
        <button className="pg-btn" disabled={page>=pages} onClick={()=>onPage(pages)}>»</button>
      </div>
    </div>
  );
}

function Toolbar({ search, onSearch, onExport, exportLoading, children }) {
  return (
    <div className="admin-toolbar">
      <div className="admin-search-box">
        <span className="search-icon">🔍</span>
        <input className="admin-search" placeholder="Search…" value={search}
          onChange={e=>onSearch(e.target.value)} />
        {search && <button className="search-clear" onClick={()=>onSearch('')}>×</button>}
      </div>
      <div className="toolbar-right">
        {children}
        <button className="btn btn-ghost btn-sm" onClick={onExport} disabled={exportLoading}>
          {exportLoading ? 'Exporting…' : '⬇ Export CSV'}
        </button>
      </div>
    </div>
  );
}

// ── Guard ─────────────────────────────────────────────────────────────
export default function AdminDashboardPage() {
  const user = JSON.parse(localStorage.getItem('hp_user')||'{}');
  if (!user.type || user.type !== 'admin') return (
    <div className="container text-center" style={{padding:80}}>
      <p style={{fontSize:48}}>🔒</p>
      <h2 style={{marginTop:16,marginBottom:8}}>Admin access only</h2>
      <p style={{color:'var(--text-secondary)',marginBottom:24}}>Please log in with your admin credentials.</p>
      <Link to="/admin/login" className="btn btn-primary btn-lg">Go to admin login →</Link>
    </div>
  );
  return <AdminContent />;
}

// ── Main ──────────────────────────────────────────────────────────────
function AdminContent() {
  const [tab, setTab]           = useState('businesses');
  const [stats, setStats]       = useState(null);
  const [emailCounts, setEmailCounts] = useState({});
  const [msg, setMsg]           = useState({ text:'', type:'success' });

  // Compose email modal
  const [emailModal, setEmailModal]     = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody]       = useState('');
  const [emailSending, setEmailSending] = useState(false);

  // History modal
  const [histModal, setHistModal]       = useState(null);
  const [histLoading, setHistLoading]   = useState(false);

  // Edit business modal
  const [editBiz, setEditBiz]     = useState(null); // business object
  const [editBizForm, setEditBizForm] = useState({});
  const [savingBiz, setSavingBiz] = useState(false);

  // Edit deal modal
  const [editDeal, setEditDeal]     = useState(null);
  const [editDealForm, setEditDealForm] = useState({});
  const [savingDeal, setSavingDeal] = useState(false);

  const showMsg = (text, type='success') => {
    setMsg({text, type});
    // Errors stay until clicked; success messages auto-dismiss after 5s
    if (type !== 'error') {
      setTimeout(() => setMsg({text:'', type:'success'}), 5000);
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const [s, ec] = await Promise.all([adminReq('/admin/stats'), adminReq('/admin/email-counts')]);
      setStats(s); setEmailCounts(ec);
    } catch {}
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  // ── Email ──
  const openEmail = (to, name, prefill='') => {
    setEmailModal({to, name});
    setEmailSubject('An invitation from halfprice.ca');
    setEmailBody(prefill || `Hi ${name},\n\nI wanted to reach out personally from halfprice.ca — Winnipeg's new restaurant deals platform.\n\nWe bring new customers to local restaurants at zero commission — no percentage taken ever.\n\nWould you be open to listing your restaurant?\n\nBest,\nDixit\nhalfprice.ca`);
  };
  const sendEmail = async () => {
    if (!emailSubject.trim()||!emailBody.trim()) return;
    setEmailSending(true);
    try {
      await adminReq('/admin/send-email',{method:'POST',body:JSON.stringify({to:emailModal.to,subject:emailSubject,body:emailBody,business_name:emailModal.name})});
      showMsg(`✓ Email sent to ${emailModal.to}`);
      setEmailModal(null); loadStats();
    } catch(err){showMsg('Email error: '+err.message,'error');}
    finally{setEmailSending(false);}
  };
  const openHistory = async (email, name) => {
    setHistModal({email,name,history:[]}); setHistLoading(true);
    try { const r=await adminReq(`/admin/email-history/${encodeURIComponent(email)}`); setHistModal({email,name,history:r}); }
    catch{} finally{setHistLoading(false);}
  };

  // ── Edit business ──
  const openEditBiz = (biz) => {
    setEditBiz(biz);
    setEditBizForm({
      business_name: biz.business_name||'',
      owner_name: biz.owner_name||'',
      phone: biz.phone||'',
      cuisine_type: biz.cuisine_type||'',
      price_range: biz.price_range||2,
      meal_types: biz.meal_types||[],
      neighbourhood: biz.neighbourhood||'',
      address: biz.address||'',
      description: biz.description||'',
      website: biz.website||'',
      instagram_handle: biz.instagram_handle||'',
      plan: biz.plan||'free',
      is_verified: biz.is_verified||false,
    });
  };
  const toggleMeal = (t) => setEditBizForm(f=>({...f,meal_types:f.meal_types.includes(t)?f.meal_types.filter(x=>x!==t):[...f.meal_types,t]}));
  const saveBiz = async () => {
    setSavingBiz(true);
    try {
      await adminReq(`/admin/businesses/${editBiz.id}`,{method:'PATCH',body:JSON.stringify(editBizForm)});
      showMsg(`✓ ${editBiz.business_name} updated`);
      setEditBiz(null);
    } catch(err){showMsg('Error: '+err.message,'error');}
    finally{setSavingBiz(false);}
  };

  // ── Edit deal ──
  const openEditDeal = (deal) => {
    setEditDeal(deal);
    setEditDealForm({
      title: deal.title||'',
      description: deal.description||'',
      what_you_get: deal.what_you_get||'',
      meal_type: deal.meal_type||'all',
      original_price: deal.original_price||'',
      deal_price: deal.deal_price||'',
      max_redemptions: deal.max_redemptions||'',
      valid_until: deal.valid_until ? deal.valid_until.split('T')[0] : '',
      terms: deal.terms||'',
      image_url: deal.image_url||'',
      is_active: deal.is_active,
      is_featured: deal.is_featured||false,
    });
  };
  const saveDeal = async () => {
    setSavingDeal(true);
    try {
      const payload = {...editDealForm,
        original_price: Number(editDealForm.original_price),
        deal_price: Number(editDealForm.deal_price),
        max_redemptions: editDealForm.max_redemptions ? Number(editDealForm.max_redemptions) : null,
        valid_until: new Date(editDealForm.valid_until).toISOString(),
      };
      await adminReq(`/admin/deals/${editDeal.id}`,{method:'PATCH',body:JSON.stringify(payload)});
      showMsg(`✓ Deal updated`);
      setEditDeal(null);
    } catch(err){showMsg('Error: '+err.message,'error');}
    finally{setSavingDeal(false);}
  };

  // ── Export ──
  const exportCSV = async (entity, search='') => {
    try {
      const token = localStorage.getItem('hp_token');
      const qs = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await fetch(`${BASE}/admin/export/${entity}${qs}`,{headers:{Authorization:`Bearer ${token}`}});
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `halfprice-${entity}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      showMsg(`✓ ${entity} exported`);
    } catch(err){showMsg('Export failed: '+err.message,'error');}
  };

  const ContactBadge = ({email}) => {
    const info = emailCounts[email];
    if (!info) return null;
    return <button className="contact-badge" onClick={()=>openHistory(email,email)} title={`Last: ${new Date(info.last_sent).toLocaleDateString('en-CA')}`}>📧 {info.count}×</button>;
  };

  const TABS = [
    {key:'businesses', label:'Restaurants'},
    {key:'deals',      label:'Deals'},
    {key:'leads',      label: stats && parseInt(stats.leads?.new_leads)>0 ? `Leads (${stats.leads.new_leads} new)` : 'Leads'},
    {key:'campaigns',  label:'Campaigns'},
    {key:'featured',   label:'Featured Slots ⭐'},
  ];

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="admin-header">
        <div className="container admin-header-inner">
          <div>
            <h1 className="admin-title">⚡ halfprice.ca Admin</h1>
            <p className="admin-subtitle">Platform management · Izenious Tech Solutions</p>
          </div>
          <button className="btn btn-ghost btn-sm admin-logout"
            onClick={()=>{localStorage.removeItem('hp_token');localStorage.removeItem('hp_user');window.location.href='/admin/login';}}>
            Log out
          </button>
        </div>
      </div>

      <div className="container admin-body">
        {/* Message */}
        {msg.text && (
          <div className={`admin-msg ${msg.type}`} onClick={()=>setMsg({text:'',type:'success'})}>
            {msg.text} <span className="msg-dismiss">× dismiss</span>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="admin-stats">
            {[
              {n: stats.businesses?.total||0,    l:'Restaurants'},
              {n: stats.businesses?.verified||0, l:'Verified'},
              {n: stats.deals?.active||0,        l:'Active deals'},
              {n: stats.redemptions?.total||0,   l:'Redemptions'},
              {n: stats.leads?.new_leads||0,     l:'New leads', highlight: parseInt(stats.leads?.new_leads)>0},
            ].map((s,i)=>(
              <div key={i} className={`admin-stat${s.highlight?' admin-stat-highlight':''}`}>
                <div className="admin-stat-num">{s.n}</div>
                <div className="admin-stat-lbl">{s.l}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="admin-tabs">
          {TABS.map(t=>(
            <button key={t.key} className={`admin-tab${tab===t.key?' active':''}`} onClick={()=>setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab==='businesses' && <BusinessesTab openEmail={openEmail} openEdit={openEditBiz} ContactBadge={ContactBadge} exportCSV={exportCSV} showMsg={showMsg} reloadStats={loadStats}/>}
        {tab==='deals'      && <DealsTab openEdit={openEditDeal} exportCSV={exportCSV} showMsg={showMsg} reloadStats={loadStats}/>}
        {tab==='leads'      && <LeadsTab openEmail={openEmail} ContactBadge={ContactBadge} exportCSV={exportCSV} showMsg={showMsg} reloadStats={loadStats}/>}
        {tab==='campaigns'  && <CampaignsTab showMsg={showMsg}/>}
        {tab==='featured'   && <FeaturedSlotsTab showMsg={showMsg}/>}
      </div>

      {/* ── COMPOSE EMAIL MODAL ── */}
      {emailModal && (
        <div className="modal-overlay" onClick={()=>setEmailModal(null)}>
          <div className="admin-modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h3>Email to {emailModal.name}</h3>
              <button className="modal-close" onClick={()=>setEmailModal(null)}>×</button>
            </div>
            <p className="modal-to">To: <strong>{emailModal.to}</strong></p>
            <div className="form-group"><label>Subject</label>
              <input className="form-control" value={emailSubject} onChange={e=>setEmailSubject(e.target.value)}/>
            </div>
            <div className="form-group"><label>Message</label>
              <textarea className="form-control" rows={9} value={emailBody} onChange={e=>setEmailBody(e.target.value)}/>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setEmailModal(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={emailSending} onClick={sendEmail}>{emailSending?'Sending…':'Send →'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EMAIL HISTORY MODAL ── */}
      {histModal && (
        <div className="modal-overlay" onClick={()=>setHistModal(null)}>
          <div className="admin-modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h3>Email history — {histModal.name}</h3>
              <button className="modal-close" onClick={()=>setHistModal(null)}>×</button>
            </div>
            <p className="modal-to">Sent to: <strong>{histModal.email}</strong></p>
            {histLoading ? <div className="skeleton" style={{height:80,borderRadius:8}}/> :
              histModal.history.length===0
                ? <p style={{color:'var(--text-muted)',fontSize:13,padding:'12px 0'}}>No emails sent yet.</p>
                : <div className="hist-list">
                    {histModal.history.map((e,i)=>(
                      <div key={i} className="hist-row">
                        <span className="hist-date">{new Date(e.sent_at).toLocaleDateString('en-CA',{month:'short',day:'numeric',year:'numeric'})} · {new Date(e.sent_at).toLocaleTimeString('en-CA',{hour:'2-digit',minute:'2-digit'})}</span>
                        <div className="hist-subject">{e.subject}</div>
                      </div>
                    ))}
                  </div>
            }
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setHistModal(null)}>Close</button>
              <button className="btn btn-primary" onClick={()=>{setHistModal(null);openEmail(histModal.email,histModal.name);}}>Send another →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT BUSINESS MODAL ── */}
      {editBiz && (
        <div className="modal-overlay" onClick={()=>setEditBiz(null)}>
          <div className="admin-modal admin-modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit — {editBiz.business_name}</h3>
              <button className="modal-close" onClick={()=>setEditBiz(null)}>×</button>
            </div>
            <div className="edit-form-grid">
              <div className="form-group"><label>Restaurant name</label>
                <input className="form-control" value={editBizForm.business_name} onChange={e=>setEditBizForm(f=>({...f,business_name:e.target.value}))}/>
              </div>
              <div className="form-group"><label>Owner name</label>
                <input className="form-control" value={editBizForm.owner_name} onChange={e=>setEditBizForm(f=>({...f,owner_name:e.target.value}))}/>
              </div>
              <div className="form-group"><label>Phone</label>
                <input className="form-control" value={editBizForm.phone} onChange={e=>setEditBizForm(f=>({...f,phone:e.target.value}))}/>
              </div>
              <div className="form-group"><label>Cuisine</label>
                <select className="form-control" value={editBizForm.cuisine_type} onChange={e=>setEditBizForm(f=>({...f,cuisine_type:e.target.value}))}>
                  {CUISINES.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Neighbourhood</label>
                <select className="form-control" value={editBizForm.neighbourhood} onChange={e=>setEditBizForm(f=>({...f,neighbourhood:e.target.value}))}>
                  <option value="">Select…</option>
                  {HOODS.map(h=><option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Price range</label>
                <select className="form-control" value={editBizForm.price_range} onChange={e=>setEditBizForm(f=>({...f,price_range:Number(e.target.value)}))}>
                  <option value={1}>$ — Budget</option><option value={2}>$$ — Moderate</option>
                  <option value={3}>$$$ — Upscale</option><option value={4}> $$$$ — Fine dining</option>
                </select>
              </div>
              <div className="form-group"><label>Plan</label>
                <select className="form-control" value={editBizForm.plan} onChange={e=>setEditBizForm(f=>({...f,plan:e.target.value}))}>
                  <option value="free">Free</option><option value="starter">Starter</option><option value="pro">Pro</option>
                </select>
              </div>
              <div className="form-group"><label>Verified</label>
                <select className="form-control" value={editBizForm.is_verified?'yes':'no'} onChange={e=>setEditBizForm(f=>({...f,is_verified:e.target.value==='yes'}))}>
                  <option value="yes">Yes ✓</option><option value="no">No — pending</option>
                </select>
              </div>
              <div className="form-group edit-form-full"><label>Meal types</label>
                <div className="meal-type-row">
                  {MEAL_TYPES.map(t=>(
                    <button type="button" key={t} className={`meal-type-btn${editBizForm.meal_types?.includes(t)?' active':''}`} onClick={()=>toggleMeal(t)}>{t}</button>
                  ))}
                </div>
              </div>
              <div className="form-group edit-form-full"><label>Address</label>
                <input className="form-control" value={editBizForm.address} onChange={e=>setEditBizForm(f=>({...f,address:e.target.value}))}/>
              </div>
              <div className="form-group edit-form-full"><label>Description</label>
                <textarea className="form-control" rows={3} value={editBizForm.description} onChange={e=>setEditBizForm(f=>({...f,description:e.target.value}))}/>
              </div>
              <div className="form-group"><label>Website</label>
                <input className="form-control" value={editBizForm.website} onChange={e=>setEditBizForm(f=>({...f,website:e.target.value}))}/>
              </div>
              <div className="form-group"><label>Instagram</label>
                <input className="form-control" value={editBizForm.instagram_handle} onChange={e=>setEditBizForm(f=>({...f,instagram_handle:e.target.value.replace('@','')}))} placeholder="handle (no @)"/>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setEditBiz(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={savingBiz} onClick={saveBiz}>{savingBiz?'Saving…':'Save changes →'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT DEAL MODAL ── */}
      {editDeal && (
        <div className="modal-overlay" onClick={()=>setEditDeal(null)}>
          <div className="admin-modal admin-modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit deal — {editDeal.business_name}</h3>
              <button className="modal-close" onClick={()=>setEditDeal(null)}>×</button>
            </div>
            <div className="edit-form-grid">
              <div className="form-group edit-form-full"><label>Title</label>
                <input className="form-control" value={editDealForm.title} onChange={e=>setEditDealForm(f=>({...f,title:e.target.value}))}/>
              </div>
              <div className="form-group edit-form-full"><label>Description</label>
                <textarea className="form-control" rows={2} value={editDealForm.description} onChange={e=>setEditDealForm(f=>({...f,description:e.target.value}))}/>
              </div>
              <div className="form-group edit-form-full"><label>What's included</label>
                <textarea className="form-control" rows={2} value={editDealForm.what_you_get} onChange={e=>setEditDealForm(f=>({...f,what_you_get:e.target.value}))}/>
              </div>
              <div className="form-group"><label>Original price ($)</label>
                <input type="number" className="form-control" value={editDealForm.original_price} onChange={e=>setEditDealForm(f=>({...f,original_price:e.target.value}))}/>
              </div>
              <div className="form-group"><label>Deal price ($)</label>
                <input type="number" className="form-control" value={editDealForm.deal_price} onChange={e=>setEditDealForm(f=>({...f,deal_price:e.target.value}))}/>
              </div>
              <div className="form-group"><label>Meal type</label>
                <select className="form-control" value={editDealForm.meal_type} onChange={e=>setEditDealForm(f=>({...f,meal_type:e.target.value}))}>
                  <option value="all">All types</option>
                  {MEAL_TYPES.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Max redemptions</label>
                <input type="number" className="form-control" placeholder="Unlimited" value={editDealForm.max_redemptions} onChange={e=>setEditDealForm(f=>({...f,max_redemptions:e.target.value}))}/>
              </div>
              <div className="form-group"><label>Valid until</label>
                <input type="date" className="form-control" value={editDealForm.valid_until} onChange={e=>setEditDealForm(f=>({...f,valid_until:e.target.value}))}/>
              </div>
              <div className="form-group"><label>Status</label>
                <select className="form-control" value={editDealForm.is_active?'active':'paused'} onChange={e=>setEditDealForm(f=>({...f,is_active:e.target.value==='active'}))}>
                  <option value="active">Active</option><option value="paused">Paused</option>
                </select>
              </div>
              <div className="form-group"><label>Featured deal</label>
                <select className="form-control" value={editDealForm.is_featured?'yes':'no'} onChange={e=>setEditDealForm(f=>({...f,is_featured:e.target.value==='yes'}))}>
                  <option value="no">No</option><option value="yes">Yes (replaces current featured)</option>
                </select>
              </div>
              <div className="form-group edit-form-full"><label>Terms</label>
                <textarea className="form-control" rows={2} value={editDealForm.terms} onChange={e=>setEditDealForm(f=>({...f,terms:e.target.value}))}/>
              </div>
              <div className="form-group edit-form-full"><label>Image URL</label>
                <input className="form-control" placeholder="https://…" value={editDealForm.image_url} onChange={e=>setEditDealForm(f=>({...f,image_url:e.target.value}))}/>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setEditDeal(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={savingDeal} onClick={saveDeal}>{savingDeal?'Saving…':'Save changes →'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── BUSINESSES TAB ────────────────────────────────────────────────────
function BusinessesTab({ openEmail, openEdit, ContactBadge, exportCSV, showMsg, reloadStats }) {
  const [data, setData] = useState({businesses:[],total:0,page:1,pages:1});
  const [search, setSearch] = useState('');
  const [sort, setSort]   = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [limit, setLimit] = useState(25);
  const [page, setPage]   = useState(1);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({search,sort,order,page,limit}).toString();
      const res = await adminReq(`/admin/businesses?${qs}`);
      setData(res);
    } catch(err){showMsg('Error: '+err.message,'error');}
    finally{setLoading(false);}
  },[search,sort,order,page,limit]);

  useEffect(()=>{load();},[load]);

  const handleSort = col => { sort===col ? setOrder(o=>o==='asc'?'desc':'asc') : (setSort(col),setOrder('desc')); setPage(1); };

  const verify = async (id,name) => {
    try{await adminReq(`/admin/businesses/${id}/verify`,{method:'POST'});showMsg(`✓ ${name} verified`);load();reloadStats();}
    catch(err){showMsg('Error: '+err.message,'error');}
  };
  const changePlan = async (id,plan) => {
    try{await adminReq(`/admin/businesses/${id}/plan`,{method:'POST',body:JSON.stringify({plan})});load();}
    catch(err){showMsg('Error: '+err.message,'error');}
  };
  const deactivate = async (id,name) => {
    if(!window.confirm(`Deactivate "${name}"?\n\nThe restaurant and its deals will be hidden from the public. Data is preserved. You can reactivate anytime.`)) return;
    try{await adminReq(`/admin/businesses/${id}`,{method:'DELETE'});showMsg(`✓ ${name} deactivated`);load();reloadStats();}
    catch(err){showMsg('Error: '+err.message,'error');}
  };
  const reactivate = async (id,name) => {
    try{await adminReq(`/admin/businesses/${id}/reactivate`,{method:'POST'});showMsg(`✓ ${name} reactivated`);load();reloadStats();}
    catch(err){showMsg('Error: '+err.message,'error');}
  };

  const doExport = async () => { setExporting(true); await exportCSV('businesses',search); setExporting(false); };
  const sp = {sort,order,onSort:handleSort};

  return (
    <div>
      <Toolbar search={search} onSearch={v=>{setSearch(v);setPage(1);}} onExport={doExport} exportLoading={exporting}/>
      {loading ? <div className="skeleton" style={{height:200,borderRadius:12,marginTop:12}}/> : (
        <>
          <div className="table-scroll">
            <table className="admin-table">
              <thead><tr>
                <SortTh col="business_name" label="Restaurant" {...sp}/>
                <SortTh col="email" label="Email" {...sp}/>
                <SortTh col="cuisine_type" label="Cuisine" {...sp}/>
                <SortTh col="neighbourhood" label="Area" {...sp}/>
                <SortTh col="plan" label="Plan" {...sp}/>
                <SortTh col="deal_count" label="Deals" {...sp}/>
                <SortTh col="total_redemptions" label="Claims" {...sp}/>
                <SortTh col="is_verified" label="Verified" {...sp}/>
                <SortTh col="created_at" label="Joined" {...sp}/>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                {data.businesses.length===0 && <tr><td colSpan={10} className="empty-cell">No restaurants found{search?` for "${search}"`:''}</td></tr>}
                {data.businesses.map(b=>(
                  <tr key={b.id} className={!b.is_active?'row-dim':''}>
                    <td>
                      <strong>{b.business_name}</strong>
                      {!b.is_active && <span className="tag-deactivated">Deactivated</span>}
                    </td>
                    <td className="cell-sm">{b.email}</td>
                    <td className="cell-sm">{b.cuisine_type}</td>
                    <td className="cell-sm">{b.neighbourhood||'—'}</td>
                    <td>
                      <select className="inline-select" value={b.plan} style={{background:PLAN_BG[b.plan]}}
                        onChange={e=>changePlan(b.id,e.target.value)}>
                        <option value="free">Free</option>
                        <option value="starter">Starter</option>
                        <option value="pro">Pro</option>
                      </select>
                    </td>
                    <td className="cell-center">{b.deal_count}</td>
                    <td className="cell-center">{b.total_redemptions}</td>
                    <td>{b.is_verified?<span className="badge badge-success">✓</span>:<button className="btn btn-primary btn-sm" onClick={()=>verify(b.id,b.business_name)}>Verify</button>}</td>
                    <td className="cell-sm">{new Date(b.created_at).toLocaleDateString('en-CA')}</td>
                    <td>
                      <div className="action-row">
                        <ContactBadge email={b.email}/>
                        <button className="act-btn act-email" onClick={()=>openEmail(b.email,b.business_name)}>Email</button>
                        <button className="act-btn act-edit" onClick={()=>openEdit(b)}>Edit</button>
                        {b.is_active
                          ? <button className="act-btn act-deactivate" onClick={()=>deactivate(b.id,b.business_name)}>Deactivate</button>
                          : <button className="act-btn act-activate" onClick={()=>reactivate(b.id,b.business_name)}>Reactivate</button>
                        }
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} pages={data.pages} total={data.total} limit={limit} onPage={setPage} onLimit={l=>{setLimit(l);setPage(1);}}/>
        </>
      )}
    </div>
  );
}

// ── DEALS TAB ─────────────────────────────────────────────────────────
function DealsTab({ openEdit, exportCSV, showMsg, reloadStats }) {
  const [data, setData] = useState({deals:[],total:0,page:1,pages:1});
  const [search, setSearch] = useState('');
  const [sort, setSort]   = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [limit, setLimit] = useState(25);
  const [page, setPage]   = useState(1);
  const [loading, setLoading]     = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async()=>{
    setLoading(true);
    try{
      const qs=new URLSearchParams({search,sort,order,page,limit}).toString();
      const res=await adminReq(`/admin/deals?${qs}`);
      setData(res);
    }catch(err){showMsg('Error: '+err.message,'error');}
    finally{setLoading(false);}
  },[search,sort,order,page,limit]);

  useEffect(()=>{load();},[load]);

  const handleSort = col=>{sort===col?setOrder(o=>o==='asc'?'desc':'asc'):(setSort(col),setOrder('desc'));setPage(1);};

  const toggle = async (id,isActive)=>{
    try{await adminReq(`/admin/deals/${id}/toggle`,{method:'POST',body:JSON.stringify({is_active:!isActive})});load();}
    catch(err){showMsg('Error: '+err.message,'error');}
  };

  const setFeatured = async (id, isFeatured, title) => {
    const action = isFeatured ? 'Remove featured from' : 'Set as featured deal';
    if (!isFeatured && !window.confirm(`Set "${title}" as the featured deal?\n\nThis will be shown as the "Deal of the day" hero on the homepage for Pro plan rotation. Any previously featured deal will be unset.`)) return;
    try {
      await adminReq(`/admin/deals/${id}`, { method:'PATCH', body: JSON.stringify({ is_featured: !isFeatured }) });
      showMsg(isFeatured ? '✓ Featured removed' : `✓ "${title}" is now the featured deal`);
      load();
    } catch(err) { showMsg('Error: '+err.message,'error'); }
  };
  const remove = async (id,title)=>{
    if(!window.confirm(`Remove "${title}"?\n\nIf customers claimed it, it will be deactivated. Otherwise fully deleted.`)) return;
    try{const r=await adminReq(`/admin/deals/${id}`,{method:'DELETE'});showMsg(`✓ ${r.message}`);load();reloadStats();}
    catch(err){showMsg('Error: '+err.message,'error');}
  };

  const sp={sort,order,onSort:handleSort};
  const expired=d=>new Date(d)<new Date();

  return (
    <div>
      <div className="info-box" style={{marginBottom:12}}>
        <strong>⭐ Featured deal (Pro plan rotation):</strong> Click <strong>☆ Feature</strong> on any deal to make it the homepage hero for Pro plan restaurants — shown as "Deal of the day" when no paid Featured Slot is active. Only one deal can be featured at a time.
        <span style={{marginLeft:12}}><strong>Paid slots take priority</strong> — if a restaurant has purchased a Featured Slot ($99–199/week), their deal overrides the Pro rotation. Manage paid slots in the ⭐ Featured Slots tab.</span>
      </div>
      <Toolbar search={search} onSearch={v=>{setSearch(v);setPage(1);}} onExport={async()=>{setExporting(true);await exportCSV('deals',search);setExporting(false);}} exportLoading={exporting}/>
      {loading?<div className="skeleton" style={{height:200,borderRadius:12,marginTop:12}}/>:(
        <>
          <div className="table-scroll">
            <table className="admin-table">
              <thead><tr>
                <SortTh col="title" label="Deal" {...sp}/>
                <SortTh col="business_name" label="Restaurant" {...sp}/>
                <SortTh col="deal_price" label="Price" {...sp}/>
                <SortTh col="discount_percent" label="%" {...sp}/>
                <SortTh col="views" label="Views" {...sp}/>
                <SortTh col="current_redemptions" label="Claimed" {...sp}/>
                <SortTh col="valid_until" label="Expires" {...sp}/>
                <SortTh col="is_active" label="Status" {...sp}/>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                {data.deals.length===0&&<tr><td colSpan={9} className="empty-cell">No deals found{search?` for "${search}"`:''}</td></tr>}
                {data.deals.map(d=>(
                  <tr key={d.id} className={`${!d.is_active?'row-dim':''} ${expired(d.valid_until)?'row-expired':''} ${d.is_featured?'row-featured':''}`}>
                    <td>
                      <strong>{d.title}</strong>
                      {d.is_featured&&<span className="badge badge-brand" style={{fontSize:10,marginLeft:6}}>⭐ Featured</span>}
                    </td>
                    <td className="cell-sm">{d.business_name}</td>
                    <td>${Number(d.deal_price).toFixed(2)}</td>
                    <td><span className="badge badge-brand">{d.discount_percent}%</span></td>
                    <td className="cell-center">{d.views}</td>
                    <td className="cell-center">{d.current_redemptions}</td>
                    <td className="cell-sm">
                      {new Date(d.valid_until).toLocaleDateString('en-CA')}
                      {expired(d.valid_until)&&<span className="tag-expired">Expired</span>}
                    </td>
                    <td>
                      <button className={`toggle-btn ${d.is_active?'active':'inactive'}`} onClick={()=>toggle(d.id,d.is_active)}>
                        {d.is_active?'Active':'Paused'}
                      </button>
                    </td>
                    <td>
                      <div className="action-row">
                        <button
                          className={`act-btn ${d.is_featured ? 'act-featured-active' : 'act-featured'}`}
                          title={d.is_featured ? 'Remove from homepage hero' : 'Set as homepage hero (Pro rotation)'}
                          onClick={()=>setFeatured(d.id, d.is_featured, d.title)}
                        >
                          {d.is_featured ? '⭐ Unfeature' : '☆ Feature'}
                        </button>
                        <button className="act-btn act-edit" onClick={()=>openEdit(d)}>Edit</button>
                        <button className="act-btn act-deactivate" onClick={()=>remove(d.id,d.title)}>Remove</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} pages={data.pages} total={data.total} limit={limit} onPage={setPage} onLimit={l=>{setLimit(l);setPage(1);}}/>
        </>
      )}
    </div>
  );
}

// ── LEADS TAB ─────────────────────────────────────────────────────────
function LeadsTab({ openEmail, ContactBadge, exportCSV, showMsg, reloadStats }) {
  const [data, setData] = useState({leads:[],total:0,page:1,pages:1});
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort]   = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [limit, setLimit] = useState(25);
  const [page, setPage]   = useState(1);
  const [loading, setLoading]     = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async()=>{
    setLoading(true);
    try{
      const qs=new URLSearchParams({search,sort,order,page,limit,status:statusFilter}).toString();
      const res=await adminReq(`/admin/leads?${qs}`);
      setData(res);
    }catch(err){showMsg('Error: '+err.message,'error');}
    finally{setLoading(false);}
  },[search,sort,order,page,limit,statusFilter]);

  useEffect(()=>{load();},[load]);

  const handleSort = col=>{sort===col?setOrder(o=>o==='asc'?'desc':'asc'):(setSort(col),setOrder('desc'));setPage(1);};

  const updateStatus=async(id,status)=>{
    try{await adminReq(`/admin/leads/${id}/status`,{method:'PATCH',body:JSON.stringify({status})});load();reloadStats();}
    catch(err){showMsg('Error: '+err.message,'error');}
  };
  const updateSource=async(id,source)=>{
    try{await adminReq(`/admin/leads/${id}/source`,{method:'PATCH',body:JSON.stringify({source})});load();}
    catch(err){showMsg('Error: '+err.message,'error');}
  };
  const deleteLead=async(id,name)=>{
    if(!window.confirm(`Permanently delete lead "${name}"? This cannot be undone.`)) return;
    try{await adminReq(`/admin/leads/${id}`,{method:'DELETE'});showMsg('✓ Lead deleted');load();reloadStats();}
    catch(err){showMsg('Error: '+err.message,'error');}
  };

  const sp={sort,order,onSort:handleSort};

  return (
    <div>
      <div className="info-box">
        <strong>📋 Leads</strong> — restaurants who showed interest but never completed registration.
        <span style={{marginLeft:12}}><strong>📧 N×</strong> = emails sent. Click to see history.</span>
      </div>
      <Toolbar search={search} onSearch={v=>{setSearch(v);setPage(1);}} onExport={async()=>{setExporting(true);await exportCSV('leads',search);setExporting(false);}} exportLoading={exporting}>
        <select className="filter-select" value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(1);}}>
          <option value="">All statuses</option>
          <option value="new">🆕 New</option>
          <option value="contacted">📞 Contacted</option>
          <option value="converted">✅ Converted</option>
          <option value="lost">❌ Lost</option>
        </select>
      </Toolbar>
      {loading?<div className="skeleton" style={{height:200,borderRadius:12,marginTop:12}}/>:(
        <>
          <div className="table-scroll">
            <table className="admin-table">
              <thead><tr>
                <SortTh col="business_name" label="Business" {...sp}/>
                <SortTh col="email" label="Email" {...sp}/>
                <th>Phone</th>
                <SortTh col="cuisine_type" label="Cuisine" {...sp}/>
                <SortTh col="source" label="How found us" {...sp}/>
                <SortTh col="status" label="Status" {...sp}/>
                <SortTh col="created_at" label="Date" {...sp}/>
                <th>Contacted</th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                {data.leads.length===0&&<tr><td colSpan={9} className="empty-cell">No leads found{search?` for "${search}"`:''}{statusFilter?` with status "${statusFilter}"`:''}</td></tr>}
                {data.leads.map(l=>(
                  <tr key={l.id}>
                    <td>
                      <strong>{l.business_name||'—'}</strong>
                      {l.owner_name&&<div className="cell-sub">{l.owner_name}</div>}
                    </td>
                    <td className="cell-sm">{l.email}</td>
                    <td className="cell-sm">{l.phone||'—'}</td>
                    <td className="cell-sm">{l.cuisine_type||'—'}</td>
                    <td>
                      <select className="inline-select" value={l.source||'website'} onChange={e=>updateSource(l.id,e.target.value)}>
                        {SOURCE_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="inline-select" value={l.status} style={{background:STATUS_BG[l.status]||STATUS_BG.new}}
                        onChange={e=>updateStatus(l.id,e.target.value)}>
                        <option value="new">🆕 New</option>
                        <option value="contacted">📞 Contacted</option>
                        <option value="converted">✅ Converted</option>
                        <option value="lost">❌ Lost</option>
                      </select>
                    </td>
                    <td className="cell-sm">{new Date(l.created_at).toLocaleDateString('en-CA')}</td>
                    <td><ContactBadge email={l.email}/></td>
                    <td>
                      <div className="action-row">
                        <button className="act-btn act-email" onClick={()=>openEmail(l.email,l.business_name||l.owner_name||l.email)}>Email</button>
                        <button className="act-btn act-deactivate" onClick={()=>deleteLead(l.id,l.business_name||l.email)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} pages={data.pages} total={data.total} limit={limit} onPage={setPage} onLimit={l=>{setLimit(l);setPage(1);}}/>
        </>
      )}
    </div>
  );
}

// ── CAMPAIGNS TAB ─────────────────────────────────────────────────────
const CAMPAIGN_TEMPLATES = {
  consumers_dormant: {
    label: '🍽 Re-engage dormant diners',
    desc: 'Diners who claimed a deal before but haven\'t in the last 14 days',
    subject: 'We miss you — fresh deals waiting at halfprice.ca',
    body: `Hi {name},\n\nIt's been a little while! There are great new restaurant deals on halfprice.ca right now — up to 50% off at Winnipeg's best local spots.\n\nCome back and grab a deal before it expires.\n\nSee you soon,\nThe halfprice.ca team`,
  },
  consumers_90d: {
    label: '📣 All recent diners',
    desc: 'Everyone who claimed a deal in the last 90 days',
    subject: 'New deals just added on halfprice.ca!',
    body: `Hi {name},\n\nWe've added fresh restaurant deals this week — up to 50% off at local Winnipeg restaurants.\n\nCheck them out before they're gone.\n\nThe halfprice.ca team`,
  },
  restaurants_free: {
    label: '⭐ Free plan → Upgrade nudge',
    desc: 'Restaurants currently on the Free plan (1 deal limit)',
    subject: 'Get more customers — upgrade your halfprice.ca plan',
    body: `Hi {name},\n\nYou're currently on the Free plan with 1 active deal. Restaurants on our Starter plan ($49/month) list up to 5 deals and get full analytics — most see noticeably more redemptions.\n\nWant to see what more deals could do for your restaurant?\n\nUpgrade anytime from your dashboard.\n\nBest,\nDixit · halfprice.ca`,
  },
  restaurants_starter: {
    label: '🚀 Starter plan → Pro nudge',
    desc: 'Restaurants currently on the Starter plan',
    subject: 'Unlock unlimited deals + featured placement on halfprice.ca',
    body: `Hi {name},\n\nYou're doing great on the Starter plan! Ready for more? Our Pro plan ($99/month) gives you unlimited active deals plus featured placement on the homepage — the very first thing every visitor sees.\n\nWant to grow further? Upgrade from your dashboard anytime.\n\nBest,\nDixit · halfprice.ca`,
  },
  restaurants_upgrade_candidates: {
    label: '💰 All Free + Starter restaurants',
    desc: 'Combined audience — every restaurant not yet on Pro',
    subject: 'Grow your restaurant\'s reach on halfprice.ca',
    body: `Hi {name},\n\nWant more customers walking through your door? Upgrading your halfprice.ca plan unlocks more active deals, full analytics, and featured placement.\n\nTake a look at your options anytime from your dashboard.\n\nBest,\nDixit · halfprice.ca`,
  },
};

function CampaignsTab({ showMsg }) {
  const [audience, setAudience] = useState(null);
  const [loadingAudience, setLoadingAudience] = useState(true);
  const [selected, setSelected] = useState('consumers_dormant');
  const [subject, setSubject] = useState(CAMPAIGN_TEMPLATES.consumers_dormant.subject);
  const [body, setBody] = useState(CAMPAIGN_TEMPLATES.consumers_dormant.body);
  const [sending, setSending] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    adminReq('/admin/campaigns/audience').then(setAudience).catch(()=>{}).finally(()=>setLoadingAudience(false));
  }, []);

  const selectTemplate = (key) => {
    setSelected(key);
    setSubject(CAMPAIGN_TEMPLATES[key].subject);
    setBody(CAMPAIGN_TEMPLATES[key].body);
    setConfirmStep(false);
  };

  const audienceCount = () => {
    if (!audience) return 0;
    const map = {
      consumers_dormant: audience.consumers_dormant,
      consumers_90d: audience.consumers_90d,
      restaurants_free: audience.restaurants_free,
      restaurants_starter: audience.restaurants_starter,
      restaurants_upgrade_candidates: audience.restaurants_upgrade_candidates,
    };
    return map[selected] || 0;
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await adminReq('/admin/campaigns/send', { method:'POST', body: JSON.stringify({ audience: selected, subject, body }) });
      showMsg(`✓ ${res.message}`);
      setConfirmStep(false);
    } catch (err) { showMsg('Error: '+err.message, 'error'); }
    finally { setSending(false); }
  };

  return (
    <div className="campaigns-tab">
      <div className="info-box">
        <strong>📧 Email campaigns</strong> — send a one-time email blast to a group of diners or restaurants. Every email is logged.
        Use this for weekly/monthly re-engagement. For automatic recurring sends, set up a weekly cron-job.org ping to a future scheduled-send endpoint (not built yet — manual send works great for getting started).
      </div>

      <div className="campaign-grid">
        <div className="campaign-templates">
          {Object.entries(CAMPAIGN_TEMPLATES).map(([key, t]) => (
            <button key={key} className={`campaign-template-card ${selected===key?'active':''}`} onClick={()=>selectTemplate(key)}>
              <div className="ctc-label">{t.label}</div>
              <div className="ctc-desc">{t.desc}</div>
              {audience && !loadingAudience && (
                <div className="ctc-count">{audience[key] ?? 0} recipients</div>
              )}
            </button>
          ))}
        </div>

        <div className="campaign-compose">
          <div className="form-group">
            <label>Audience</label>
            <div className="campaign-audience-badge">
              {loadingAudience ? 'Loading…' : `${audienceCount()} recipients will receive this email`}
            </div>
          </div>
          <div className="form-group">
            <label>Subject</label>
            <input className="form-control" value={subject} onChange={e=>setSubject(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Message <span style={{fontWeight:400,color:'var(--text-muted)'}}>— use {'{name}'} to personalize</span></label>
            <textarea className="form-control" rows={10} value={body} onChange={e=>setBody(e.target.value)} />
          </div>

          {!confirmStep ? (
            <button className="btn btn-primary" disabled={audienceCount()===0} onClick={()=>setConfirmStep(true)}>
              Review & send →
            </button>
          ) : (
            <div className="campaign-confirm">
              <p>⚠ This will send <strong>{audienceCount()} emails</strong> right now. This cannot be undone.</p>
              <div style={{display:'flex',gap:10}}>
                <button className="btn btn-ghost" onClick={()=>setConfirmStep(false)}>Cancel</button>
                <button className="btn btn-primary" disabled={sending} onClick={send}>
                  {sending ? 'Sending…' : `Yes, send to ${audienceCount()} people →`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── FEATURED SLOTS TAB ────────────────────────────────────────────────
function FeaturedSlotsTab({ showMsg }) {
  const [calendar, setCalendar]     = useState([]);
  const [slots, setSlots]           = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editSlot, setEditSlot]     = useState(null); // slot being edited for payment status

  const [form, setForm] = useState({
    business_id: '', deal_id: '', start_date: '', end_date: '',
    price_paid: '149', payment_status: 'pending', payment_method: '', payment_ref: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const setF = (k,v) => setForm(f => ({...f, [k]:v}));

  const selectedBiz = businesses.find(b => b.id === form.business_id);
  const availableDeals = selectedBiz?.deals || [];

  const load = async () => {
    setLoading(true);
    try {
      const [cal, slotsRes, bizRes] = await Promise.all([
        adminReq('/featured-slots/calendar'),
        adminReq('/featured-slots'),
        adminReq('/featured-slots/businesses-with-deals'),
      ]);
      setCalendar(cal.weeks || []);
      setSlots(slotsRes.slots || []);
      setBusinesses(bizRes.businesses || []);
    } catch(err) { showMsg('Error: '+err.message, 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleBook = async (e) => {
    e.preventDefault();
    if (!form.business_id || !form.deal_id || !form.start_date || !form.end_date)
      return showMsg('Please fill all required fields', 'error');
    setSaving(true);
    try {
      const payload = { ...form, price_paid: parseFloat(form.price_paid) };
      delete payload._editId;

      if (form._editId) {
        // Edit existing slot
        await adminReq(`/featured-slots/${form._editId}`, { method:'PATCH', body: JSON.stringify(payload) });
        showMsg('✓ Featured slot updated');
      } else {
        // Create new slot
        await adminReq('/featured-slots', { method:'POST', body: JSON.stringify(payload) });
        showMsg('✓ Featured slot booked successfully');
      }
      setShowForm(false);
      setForm({ business_id:'', deal_id:'', start_date:'', end_date:'', price_paid:'149', payment_status:'pending', payment_method:'', payment_ref:'', notes:'' });
      load();
    } catch(err) { showMsg('Error: '+err.message, 'error'); }
    finally { setSaving(false); }
  };

  const markPaid = async (id, method, ref) => {
    try {
      await adminReq(`/featured-slots/${id}`, { method:'PATCH', body: JSON.stringify({ payment_status:'paid', payment_method: method, payment_ref: ref }) });
      showMsg('✓ Marked as paid'); setEditSlot(null); load();
    } catch(err) { showMsg('Error: '+err.message, 'error'); }
  };

  const cancelSlot = async (id, name) => {
    if (!window.confirm(`Cancel featured slot for ${name}? This marks it as refunded.`)) return;
    try {
      await adminReq(`/featured-slots/${id}`, { method:'DELETE' });
      showMsg('✓ Slot cancelled'); load();
    } catch(err) { showMsg('Error: '+err.message, 'error'); }
  };

  const STATUS_COLORS = { pending:'#FEF3C7', paid:'#E3F4EB', refunded:'#F3F4F6' };
  const STATUS_TEXT   = { pending:'#92400E', paid:'#1A5C35', refunded:'#6B7280' };

  // Safe date formatting - handles both string "YYYY-MM-DD" and Date objects
  const fmtDate = (d, opts = { month:'short', day:'numeric' }) => {
    if (!d) return '—';
    const str = typeof d === 'string' ? d : d.toISOString().split('T')[0];
    return new Date(str + 'T12:00:00').toLocaleDateString('en-CA', opts);
  };

  // Prefill dates for a week slot
  const prefillWeek = (start) => {
    const end = new Date(start + 'T12:00:00');
    end.setDate(end.getDate() + 6);
    setF('start_date', start);
    setF('end_date', end.toISOString().split('T')[0]);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Open form to edit existing slot
  const openEdit = (slot) => {
    setForm({
      business_id: slot.business_id,
      deal_id: slot.deal_id,
      start_date: typeof slot.start_date === 'string' ? slot.start_date : slot.start_date.toISOString().split('T')[0],
      end_date: typeof slot.end_date === 'string' ? slot.end_date : slot.end_date.toISOString().split('T')[0],
      price_paid: slot.price_paid,
      payment_status: slot.payment_status,
      payment_method: slot.payment_method || '',
      payment_ref: slot.payment_ref || '',
      notes: slot.notes || '',
      _editId: slot.id,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalRevenue = slots.filter(s=>s.payment_status==='paid').reduce((t,s)=>t+parseFloat(s.price_paid),0);
  const paidCount    = slots.filter(s=>s.payment_status==='paid').length;
  const pendingCount = slots.filter(s=>s.payment_status==='pending').length;

  return (
    <div className="fs-tab">
      <div className="info-box">
        <strong>⭐ Featured Slot Bookings</strong> — manage exclusive homepage hero placements. When a slot is active and <strong>paid</strong>, that restaurant's deal automatically appears as the hero on halfprice.ca. Book slots here after collecting payment from the restaurant via email.
      </div>

      {/* Revenue summary — always shown */}
      <div className="fs-revenue-row">
        <div className="fs-rev-card">
          <div className="fs-rev-num">${totalRevenue.toFixed(0)}</div>
          <div className="fs-rev-lbl">Total revenue (CAD)</div>
        </div>
        <div className="fs-rev-card">
          <div className="fs-rev-num">{paidCount}</div>
          <div className="fs-rev-lbl">Paid bookings</div>
        </div>
        <div className="fs-rev-card">
          <div className="fs-rev-num">{pendingCount}</div>
          <div className="fs-rev-lbl">Pending payment</div>
        </div>
        <div className="fs-rev-card">
          <button className="btn btn-primary" onClick={() => { setForm({ business_id:'', deal_id:'', start_date:'', end_date:'', price_paid:'149', payment_status:'pending', payment_method:'', payment_ref:'', notes:'' }); setShowForm(true); }}>
            + Book new slot
          </button>
        </div>
      </div>

      {/* Book slot form */}
      {showForm && (
        <div className="fs-form-card">
          <div className="fs-form-header">
            <h3>{form._editId ? 'Edit featured slot' : 'Book a featured slot'}</h3>
            <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
          </div>
          <form onSubmit={handleBook}>
            <div className="edit-form-grid">
              <div className="form-group">
                <label>Restaurant *</label>
                <select className="form-control" value={form.business_id} onChange={e => { setF('business_id', e.target.value); setF('deal_id', ''); }} required>
                  <option value="">Select restaurant…</option>
                  {businesses.map(b => (
                    <option key={b.id} value={b.id}>{b.business_name} ({b.plan} plan)</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Deal to feature *</label>
                <select className="form-control" value={form.deal_id} onChange={e => setF('deal_id', e.target.value)} required disabled={!form.business_id}>
                  <option value="">Select deal…</option>
                  {availableDeals.map(d => (
                    <option key={d.id} value={d.id}>{d.title} — {d.discount_percent}% off (${d.deal_price})</option>
                  ))}
                </select>
                {form.business_id && availableDeals.length === 0 && (
                  <span className="form-hint" style={{color:'var(--brand)'}}>This restaurant has no active deals. Ask them to create one first.</span>
                )}
              </div>
              <div className="form-group">
                <label>Start date (Monday) *</label>
                <input type="date" className="form-control" value={form.start_date} onChange={e => setF('start_date', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>End date (Sunday) *</label>
                <input type="date" className="form-control" value={form.end_date} onChange={e => setF('end_date', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Price agreed ($CAD) *</label>
                <input type="number" className="form-control" value={form.price_paid} onChange={e => setF('price_paid', e.target.value)} min="99" max="199" required />
              </div>
              <div className="form-group">
                <label>Payment status</label>
                <select className="form-control" value={form.payment_status} onChange={e => setF('payment_status', e.target.value)}>
                  <option value="pending">Pending — awaiting payment</option>
                  <option value="paid">Paid ✓</option>
                </select>
              </div>
              <div className="form-group">
                <label>Payment method</label>
                <select className="form-control" value={form.payment_method} onChange={e => setF('payment_method', e.target.value)}>
                  <option value="">Select…</option>
                  <option value="e-transfer">Interac e-Transfer</option>
                  <option value="stripe">Stripe (manual charge)</option>
                  <option value="cheque">Cheque</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Payment reference #</label>
                <input className="form-control" placeholder="e-transfer ref, Stripe ID, etc." value={form.payment_ref} onChange={e => setF('payment_ref', e.target.value)} />
              </div>
              <div className="form-group edit-form-full">
                <label>Notes (internal)</label>
                <textarea className="form-control" rows={2} placeholder="e.g. Contacted by restaurant owner on June 15, agreed $149 for week of June 30" value={form.notes} onChange={e => setF('notes', e.target.value)} />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : form._editId ? 'Save changes →' : 'Book slot →'}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="skeleton" style={{height:300, borderRadius:12, marginTop:16}}/> : (
        <>
          {/* 12-week calendar */}
          <div className="fs-section-title">12-week calendar</div>
          <div className="fs-calendar">
            {calendar.map((w,i) => {
              const isThisWeek = w.start <= new Date().toISOString().split('T')[0] && w.end >= new Date().toISOString().split('T')[0];
              const slotStatus = w.slot?.payment_status;
              return (
                <div key={i} className={`fs-week ${!w.slot ? 'free' : slotStatus === 'paid' ? 'booked-paid' : 'booked-pending'} ${isThisWeek ? 'current-week' : ''}`}>
                  <div className="fsw-dates">
                    {fmtDate(w.start)} – {fmtDate(w.end)}
                    {isThisWeek && <span className="fsw-now">This week</span>}
                  </div>
                  {w.slot ? (
                    <div className="fsw-booked">
                      <span className="fsw-biz">{w.slot.business_name}</span>
                      <span className="fsw-status" style={{background: STATUS_COLORS[slotStatus], color: STATUS_TEXT[slotStatus]}}>
                        {slotStatus}
                      </span>
                    </div>
                  ) : (
                    <button className="fsw-book-btn" onClick={() => prefillWeek(w.start)}>
                      + Book this week
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* All bookings list */}
          <div className="fs-section-title" style={{marginTop:28}}>All bookings</div>
          {slots.length === 0 ? (
            <div className="empty-cell" style={{padding:'40px 20px', textAlign:'center', color:'var(--text-muted)'}}>
              No featured slots booked yet.
              <button className="btn btn-primary btn-sm" style={{marginLeft:16}} onClick={() => setShowForm(true)}>Book first slot →</button>
            </div>
          ) : (
            <div className="table-scroll">
              <table className="admin-table">
                <thead><tr>
                  <th>Restaurant</th><th>Deal</th><th>Dates</th><th>Price</th>
                  <th>Payment</th><th>Ref #</th><th>Notes</th><th>Actions</th>
                </tr></thead>
                <tbody>
                  {slots.map(s => (
                    <tr key={s.id} style={{opacity: s.payment_status==='refunded' ? 0.5 : 1}}>
                      <td><strong style={{fontSize:13}}>{s.business_name}</strong><div className="cell-sub">{s.business_email}</div></td>
                      <td className="cell-sm">{s.deal_title}</td>
                      <td className="cell-sm">
                        {fmtDate(s.start_date)} – {fmtDate(s.end_date, {month:'short',day:'numeric',year:'numeric'})}
                      </td>
                      <td>${parseFloat(s.price_paid).toFixed(0)} CAD</td>
                      <td>
                        <span style={{background: STATUS_COLORS[s.payment_status], color: STATUS_TEXT[s.payment_status], padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:500}}>
                          {s.payment_status}
                        </span>
                        {s.payment_method && <div className="cell-sub">{s.payment_method}</div>}
                      </td>
                      <td className="cell-sm">{s.payment_ref || '—'}</td>
                      <td className="cell-sm">{s.notes || '—'}</td>
                      <td>
                        <div className="action-row">
                          <button className="act-btn act-edit" onClick={() => openEdit(s)}>Edit</button>
                          {s.payment_status === 'pending' && (
                            <button className="act-btn act-activate" onClick={() => {
                              const method = prompt('Payment method (e-transfer / stripe / cash / cheque):') || '';
                              const ref    = prompt('Reference number (optional):') || '';
                              if (method !== null) markPaid(s.id, method, ref);
                            }}>Mark paid</button>
                          )}
                          {s.payment_status !== 'refunded' && (
                            <button className="act-btn act-deactivate" onClick={() => cancelSlot(s.id, s.business_name)}>Cancel</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
